import type { Command } from "commander";

/**
 * Recursively walk a commander program tree and collect all static candidates
 * for a given word position.
 *
 * @param program - The root Commander program instance.
 * @param words - The words typed so far (including the binary name as `words[0]`).
 * @returns Array of candidate strings that start with the current partial word.
 * @docLink cli/dev-guide#completion
 */
export function getStaticCandidates(program: Command, words: string[]): string[] {
  // words[0] is always the binary name ('skaile'), skip it
  const relevant = words.slice(1);

  let current: Command = program;
  for (let i = 0; i < relevant.length - 1; i++) {
    const word = relevant[i]!;
    const sub = current.commands.find((c) => c.name() === word || c.aliases().includes(word));
    if (!sub) break;
    current = sub;
  }

  const last = relevant[relevant.length - 1] ?? "";

  // If last word starts with -, suggest flags; otherwise suggest subcommands + flags
  const candidates: string[] = [
    ...current.commands.map((c) => c.name()),
    ...current.options.flatMap((o) => [o.short, o.long].filter(Boolean) as string[]),
  ];

  return candidates.filter((c) => c.startsWith(last));
}

/**
 * Shell script templates — one per shell.
 * The script calls the CLI's `complete -- $cword $words` form at tab time.
 *
 * @docLink cli/dev-guide#completion
 */
export const SHELL_SCRIPTS: Record<string, (bin: string) => string> = {
  zsh: (bin) => `\
_${bin}_completion() {
  local words=("\${words[@]}")
  local cword=$((CURRENT - 1))
  local completions
  completions=$(${bin} complete -- $cword "\${words[@]}" 2>/dev/null)
  local -a items
  items=(\${(f)completions})
  compadd -a items
}
compdef _${bin}_completion ${bin}`,

  bash: (bin) => `\
_${bin}_completion() {
  local cword=$COMP_CWORD
  local words=("\${COMP_WORDS[@]}")
  local completions
  completions=$(${bin} complete -- "$cword" "\${words[@]}" 2>/dev/null)
  COMPREPLY=($(compgen -W "$completions" -- "\${COMP_WORDS[$COMP_CWORD]}"))
}
complete -F _${bin}_completion ${bin}`,

  fish: (bin) => `\
function __${bin}_complete
  set -l words (commandline -opc)
  set -l cword (count $words)
  ${bin} complete -- $cword $words 2>/dev/null
end
complete -c ${bin} -f -a '(__${bin}_complete)'`,
};
